import { useFrame } from '@react-three/fiber';
import { useEffect, useMemo, useState } from 'react';
import * as THREE from 'three';
import { MeshStandardNodeMaterial } from 'three/webgpu';
import { loadNeighbours, type SiteMeshes } from './neighbours';
import { useWorldStore } from './state';

const GROUND_RADIUS = 420;
const forward = new THREE.Vector3();

const heading = (x: number, z: number) => Math.atan2(x, -z);

// The site around the hero: a ground that takes shadows, the neighbours and the streets, turned
// so their north is the model's true north.
export function WorldScene() {
  const groundY = useWorldStore((s) => s.groundY);
  const yaw = useWorldStore((s) => s.yaw);
  const radius = useWorldStore((s) => s.radius);
  const [site, setSite] = useState<SiteMeshes | null>(null);
  const ground = useMemo(() => {
    const m = new MeshStandardNodeMaterial();
    m.color.set('#2c3038');
    m.roughness = 1;
    m.metalness = 0;
    return m;
  }, []);

  useEffect(() => {
    if (radius <= 0) return;
    let live = true;
    void loadNeighbours(radius).then((s) => {
      if (!live) return;
      setSite(s);
      useWorldStore.getState().setNeighbours(s.source, s.origin);
    });
    return () => {
      live = false;
    };
  }, [radius]);

  useEffect(() => () => ground.dispose(), [ground]);

  useFrame(({ camera }) => {
    camera.getWorldDirection(forward);
    const north = heading(-Math.sin(yaw), -Math.cos(yaw));
    const deg = THREE.MathUtils.radToDeg(north - heading(forward.x, forward.z));
    useWorldStore.getState().setCompass(THREE.MathUtils.euclideanModulo(deg + 180, 360) - 180);
  });

  return (
    <>
      <mesh
        rotation-x={-Math.PI / 2}
        position-y={groundY - 0.01}
        material={ground}
        receiveShadow
        raycast={() => undefined}
      >
        <circleGeometry args={[GROUND_RADIUS, 96]} />
      </mesh>
      {site && (
        <group position-y={groundY} rotation-y={yaw}>
          <primitive object={site.buildings} />
          {site.streets && <primitive object={site.streets} />}
        </group>
      )}
    </>
  );
}
